import React, { useState, useEffect } from "react";
import { FileText, Plus, Edit2, Trash2, PenTool } from "lucide-react";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Input from "../../components/common/form/input/InputField";
import TextArea from "../../components/common/form/input/TextArea";
import { Modal } from "../../components/common/ui/modal";
import { Card } from "../../components/common/Card";
import { useOrg } from "../../contexts/OrgContext";
import {
  PdfManagementProvider,
  usePdfManagement,
  BrowserPdfDocument,
} from "../../contexts/PdfManagementContext";
import { PdfTemplateManager } from "../../components/PdfTemplateManager";
import { MarkdownEditor } from "../../components/MarkdownEditor";
import type {
  FormField,
  FormFieldType,
  RequestType,
} from "@secure-caseworker/shared";
import type { SignerRole, RoutingType } from "../../types/request";
import { useToast } from "../../contexts/ToastContext";

export interface FormTemplateData {
  type: RequestType;
  title: string;
  description?: string;
  instructions?: string;
  fields: FormField[];
  pdfDocuments?: BrowserPdfDocument[];
  signerRoles?: SignerRole[];
  routing?: RoutingType;
}

export interface TemplateValidationResult {
  isValid: boolean;
  error?: string;
}

interface FormTemplateEditorProps {
  initialData?: FormTemplateData;
  templateId?: string; // Required for PDF uploads when editing existing templates
  onSubmit?: (data: FormTemplateData) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
  defaultType?: RequestType;
  /** If true, the type toggle is disabled (for editing existing templates) */
  typeDisabled?: boolean;
  /** If true, hides submit/cancel buttons (parent controls submission via onChange) */
  embedded?: boolean;
  /** Called when form data changes. In embedded mode, use validation result to track validity. */
  onChange?: (
    data: FormTemplateData,
    validation: TemplateValidationResult,
  ) => void;
}

const FormTemplateEditorContent = 
  ({
    initialData,
    onSubmit,
    onCancel,
    submitLabel = "Create Template",
    isLoading = false,
    defaultType = "form" as RequestType,
    typeDisabled = false,
    embedded = false,
    onChange,
  }: FormTemplateEditorProps) => {
    const { showToast } = useToast();
    const { pdfDocuments, signerRoles, routing } = usePdfManagement();

    // Template type state
    const [templateType, setTemplateType] = useState<RequestType>(
      initialData?.type || defaultType,
    );

    // Form state - initialize with empty values
    const [formTitle, setFormTitle] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formInstructions, setFormInstructions] = useState("");
    const [fields, setFields] = useState<FormField[]>([]);

    // Modals
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [editingField, setEditingField] = useState<FormField | null>(null);
    const [fieldLabel, setFieldLabel] = useState("");
    const [fieldType, setFieldType] = useState<FormFieldType>("text");
    const [fieldRequired, setFieldRequired] = useState(false);
    const [fieldPlaceholder, setFieldPlaceholder] = useState("");
    const [fieldOptions, setFieldOptions] = useState("");

    // Update form when initialData changes
    useEffect(() => {
      if (initialData) {
        setTemplateType(initialData.type || defaultType);
        setFormTitle(initialData.title || "");
        setFormDescription(initialData.description || "");
        setFormInstructions(initialData.instructions || "");
        setFields(initialData.fields || []);
        // PDF loading is now handled by PdfManagementProvider
      }
    }, [initialData, defaultType]);

    // Helper to build form data object
    const buildFormData = (): FormTemplateData => ({
      type: templateType,
      title: formTitle,
      description: formDescription || undefined,
      instructions: formInstructions || undefined,
      fields: templateType === "form" ? fields : [],
      pdfDocuments: templateType === "signature" ? pdfDocuments : undefined,
      signerRoles: templateType === "signature" ? signerRoles : undefined,
      routing: templateType === "signature" ? routing : undefined,
    });

    // Validate form and return error message if invalid
    const validateForm = (): string | null => {
      if (!formTitle.trim()) {
        return "Template title is required";
      }
      if (templateType === "form" && fields.length === 0) {
        return "Please add at least one form field";
      }
      if (templateType === "signature") {
        if (pdfDocuments.length === 0) {
          return "Please upload at least one PDF document for signatures";
        }
        for (const doc of pdfDocuments) {
          if (doc.signaturePlaces.length === 0) {
            return `Please add at least one signature field to "${doc.file?.name}"`;
          }
        }
      }
      return null;
    };

    // Notify parent of changes with validation result
    useEffect(() => {
      if (onChange) {
        const error = validateForm();
        onChange(buildFormData(), {
          isValid: !error,
          error: error ?? undefined,
        });
      }
    }, [
      templateType,
      formTitle,
      formDescription,
      formInstructions,
      fields,
      pdfDocuments,
      signerRoles,
      routing,
    ]);

    const handleAddField = () => {
      if (!fieldLabel.trim()) {
        showToast("Field label is required", "error");

        return;
      }

      const newField: FormField = {
        id: editingField?.id || Date.now().toString(),
        field_type: fieldType,
        label: fieldLabel,
        required: fieldRequired,
        placeholder: fieldPlaceholder || undefined,
        options: fieldOptions
          ? fieldOptions.split(",").map((o) => o.trim())
          : undefined,
      };

      if (editingField) {
        setFields(fields.map((f) => (f.id === editingField.id ? newField : f)));
        setEditingField(null);
      } else {
        setFields([...fields, newField]);
      }

      // Reset field editor
      setFieldLabel("");
      setFieldType("text");
      setFieldRequired(false);
      setFieldPlaceholder("");
      setFieldOptions("");
      setShowFieldsModal(false);
    };

    const handleEditField = (field: FormField) => {
      setEditingField(field);
      setFieldLabel(field.label);
      setFieldType(field.field_type);
      setFieldRequired(field.required);
      setFieldPlaceholder(field.placeholder || "");
      setFieldOptions(field.options?.join(", ") || "");
      setShowFieldsModal(true);
    };

    const handleRemoveField = (fieldId: string) => {
      setFields(fields.filter((f) => f.id !== fieldId));
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // Type-specific validation
      if (templateType === "form") {
        if (fields.length === 0) {
          showToast("Please add at least one form field", "error");

          return;
        }
      } else {
        // Signature type
        if (pdfDocuments.length === 0) {
          showToast(
            "Please upload at least one PDF document for signatures",
            "error",
          );

          return;
        }

        // Validate that PDFs have signature places configured
        for (const doc of pdfDocuments) {
          if (doc.signaturePlaces.length === 0) {
            showToast(
              `Please add at least one signature field to "${doc.file?.name}"`,
              "error",
            );
            return;
          }
        }
      }

      try {
        // Submit form with type-specific data (only if onSubmit provided)
        if (onSubmit) {
          await onSubmit(buildFormData());
        }
      } catch (error) {
        // Error already shown by uploadPdfs
        console.error("Submit failed:", error);
      }
    };

    const openAddFieldModal = () => {
      setEditingField(null);
      setFieldLabel("");
      setFieldType("text");
      setFieldRequired(false);
      setFieldPlaceholder("");
      setFieldOptions("");
      setShowFieldsModal(true);
    };
    return (
      <>
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Type Toggle */}
            {!typeDisabled && (
              <div>
                <Label>Template Type</Label>
                <div className="mt-2 inline-flex rounded-lg border border-gray-200 p-1 dark:border-gray-700">
                  <button
                    type="button"
                    disabled={typeDisabled}
                    onClick={() => setTemplateType("form" as RequestType)}
                    className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      templateType === "form"
                        ? "bg-brand-500 text-white"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                    } ${typeDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <FileText size={16} />
                    Form
                  </button>
                  <button
                    type="button"
                    disabled={typeDisabled}
                    onClick={() => setTemplateType("signature" as RequestType)}
                    className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      templateType === "signature"
                        ? "bg-brand-500 text-white"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                    } ${typeDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <PenTool size={16} />
                    Signature
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {templateType === "form"
                    ? "Collect information from recipients using form fields"
                    : "Request signatures on PDF documents"}
                </p>
              </div>
            )}

            <div>
              <Label>
                Title <span className="text-error-500">*</span>
              </Label>
              <Input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                required
                placeholder="Enter template title"
              />
            </div>

            {!embedded && (
              <div>
                <Label>Description (Internal)</Label>
                <TextArea
                  value={formDescription}
                  onChange={setFormDescription}
                  placeholder="Internal notes about this template (not shown to participants)"
                  rows={2}
                  hint={`${formDescription.length}/500 characters`}
                />
              </div>
            )}

            <div>
              <Label>Instructions (Customer-Facing)</Label>
              <MarkdownEditor
                value={formInstructions}
                onChange={setFormInstructions}
                placeholder="Instructions for participants filling out this request (supports markdown)"
              />
            </div>

            {/* PDF Template Upload Section - Only for Signature type */}
            {templateType === "signature" && <PdfTemplateManager />}

            {/* Form Fields Section - Only for Form type */}
            {templateType === "form" && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <Label>Form Fields</Label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={openAddFieldModal}
                    startIcon={<Plus size={16} />}
                  >
                    Add Field
                  </Button>
                </div>

                {fields.length === 0 ? (
                  <Card variant="border" className="text-center">
                    <FileText
                      size={48}
                      className="mx-auto mb-4 opacity-30 text-gray-400"
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No fields added yet. Click "Add Field" to get started.
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <Card key={field.id}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <span className="font-medium text-black dark:text-white">
                                {index + 1}. {field.label}
                              </span>
                              {field.required && (
                                <span className="text-xs text-error-500">
                                  (Required)
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Type: {field.field_type}
                              {field.placeholder &&
                                ` • Placeholder: "${field.placeholder}"`}
                              {field.options &&
                                ` • Options: ${field.options.join(", ")}`}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditField(field)}
                              className="rounded-lg border border-gray-300 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                              title="Edit field"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveField(field.id)}
                              className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                              title="Remove field"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Hide buttons in embedded mode - parent controls submission */}
            {!embedded && (
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isLoading ||
                    (templateType === "form" && fields.length === 0) ||
                    (templateType === "signature" && pdfDocuments.length === 0)
                  }
                >
                  {isLoading ? "Saving..." : submitLabel}
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Add/Edit Field Modal */}
        <Modal
          isOpen={showFieldsModal}
          onClose={() => {
            setShowFieldsModal(false);
            setEditingField(null);
          }}
          title={editingField ? "Edit Field" : "Add Field"}
          maxWidth="500px"
        >
          <div className="space-y-5">
            <div>
              <Label>
                Field Label <span className="text-error-500">*</span>
              </Label>
              <Input
                type="text"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                placeholder="e.g., Full Name, Email Address"
              />
            </div>

            <div>
              <Label>
                Field Type <span className="text-error-500">*</span>
              </Label>
              <select
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value as FormFieldType)}
                className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="tel">Phone (Tel)</option>
                <option value="date">Date</option>
                <option value="select">Select Dropdown</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio Button</option>
                <option value="file">File Upload</option>
              </select>
            </div>

            <div>
              <Label>Placeholder Text</Label>
              <Input
                type="text"
                value={fieldPlaceholder}
                onChange={(e) => setFieldPlaceholder(e.target.value)}
                placeholder="Optional placeholder text"
              />
            </div>

            {(fieldType === "select" ||
              fieldType === "radio" ||
              fieldType === "checkbox") && (
              <div>
                <Label>Options (comma-separated)</Label>
                <Input
                  type="text"
                  value={fieldOptions}
                  onChange={(e) => setFieldOptions(e.target.value)}
                  placeholder="e.g., Option 1, Option 2, Option 3"
                />
              </div>
            )}

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fieldRequired}
                  onChange={(e) => setFieldRequired(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Required field
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowFieldsModal(false);
                  setEditingField(null);
                }}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleAddField}>
                {editingField ? "Update Field" : "Add Field"}
              </Button>
            </div>
          </div>
        </Modal>
      </>
    );
  };

export const RequestTemplateEditor = (props: FormTemplateEditorProps) => {
  const { orgId } = useOrg();

  if (!orgId) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <p className="text-error-500">Error: Organization not found</p>
      </div>
    );
  }

  return (
    <PdfManagementProvider
      initialPdfDocuments={props.initialData?.pdfDocuments}
      initialSignerRoles={props.initialData?.signerRoles}
      initialRouting={props.initialData?.routing}
    >
      <FormTemplateEditorContent {...props} />
    </PdfManagementProvider>
  );
};

// Alias for backward compatibility
export const FormTemplateEditor = RequestTemplateEditor;
